"use client";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback } from "react";
import verified from "#/assets/badge/verified-badge.svg";
import type { ApiResponse, PaginatedResult } from "@/lib/fetch";
import type { Consultant, Expertise } from "@/types/consulting";
import ExtendedImage from "../ui/extended-image";
import FilterSelect from "../ui/filter-select";

const ExpertList = ({
  consultingId,
  expertises,
}: {
  expertises: Expertise[];
  consultingId: string;
}) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const expertiseId = searchParams.get("expertise");
  const expertise = expertises.find((item) => item.id === expertiseId);
  const cityId = searchParams.get("city");
  const city = expertise?.cities.find((item) => item.id === cityId);
  
  // biome-ignore lint/correctness/useHookAtTopLevel: <explanation>
  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);

      return params.toString();
    },
    [searchParams],
  );
  const { data, isLoading } = useQuery({
    queryFn: async () => {
      const url = `/api/consulting/${consultingId}/consultants?ExpertiseId=${expertiseId}&CityId=${cityId}`;
      const response = await axios
        .get<ApiResponse<PaginatedResult<Consultant>>>(url)
        .then((res) => res.data);
      if (!response.isSuccess) {
        return [];
      }
      return response.result.data;
    },
    queryKey: ["experts", consultingId, expertiseId, cityId],
  });
  if (isLoading) {
    return "loading...";
  }

  return (
    <div className="flex flex-col justify-start items-start gap-4">
      <div className="flex flex-row gap-4">
        <FilterSelect
          label="Uzmanlık"
          onChange={(e: string) => {
            router.push(`${pathname}?${createQueryString("expertise", e)}`);
          }}
          options={expertises}
          value={expertise?.id}
        />
        <FilterSelect
          label="Şehir"
          onChange={(e) => {
            router.push(`${pathname}?${createQueryString("city", e)}`);
          }}
          options={expertise?.cities || []}
          value={city?.id}
        />
      </div>
      <div className="flex flex-row gap-2 justify-start items-start w-full flex-wrap">
        {data?.map((expert) => (
          <div
            className="border rounded-2xl p-2 w-44 flex flex-col gap-2 border-gray-300 hover:border-brand-blue-600 hover:bg-brand-blue-100 cursor-pointer"
            key={expert.id}
          >
            <ExtendedImage
              alt={expert.name}
              className="rounded-xl"
              height={176}
              src={expert.imageUrl}
              width={176}
            />
            <div className="text-[12px] leading-4.5 font-semibold">
              <h3 className="inline-flex justify-center items-center gap-1">
                {expert.name}
                <ExtendedImage
                  alt="verified user"
                  height={16}
                  src={verified}
                  width={16}
                />
              </h3>
              <h3 className="font-medium opacity-90">
                {expert.expertises.at(0)}
              </h3>
            </div>
            <p className="text-[12px] leading-4.5 font-medium line-clamp-4">
              {expert.bio}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExpertList;
